// src/tabs/logements/_ficheUi.jsx — primitives DA « fiche logement » (CHANTIER 65).
//
// Patron §8 ter (DESIGN.md) — fiche + accordéons : sections en blanc franc avec
// pastille d'icône de TEINTE-CATÉGORIE FIXE (repère de nav, jamais un signal
// d'état) + alertes en lignes fines à liséré gauche (jamais des encadrés empilés).
//
// Local à l'espace « Logements » (InfoFbzTab + onglets sœurs). NE réutilise PAS
// le SectionCard partagé d'atoms.jsx (consommé aussi par le lot Livret) → aucun
// effet de bord cross-lot. Présentation pure : zéro logique, zéro donnée.

import { ChevronDown, AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react'
import { DS } from '../../lib/ui'

// Mapping VALIDÉ teinte-catégorie → token DS (§2). Constant et permanent :
// la couleur = un sens (« cette teinte = cette section »), pas une décoration.
export const CAT_TINTS = {
  ciel:    { bg: DS.catCielBg,    ico: DS.catCielIco },
  indigo:  { bg: DS.catIndigoBg,  ico: DS.catIndigoIco },
  corail:  { bg: DS.catCorailBg,  ico: DS.catCorailIco },
  ardoise: { bg: DS.catArdoiseBg, ico: DS.catArdoiseIco },
  rose:    { bg: DS.catRoseBg,    ico: DS.catRoseIco },
}

// Pastille d'icône de section (carrée douce, §8 quater). `icon` = composant lucide.
// Règle #14 : sur en-tête OUVERT (fond marine plein), la pastille passe en
// verre dépoli blanc + icône blanche (lisibilité) ; FERMÉ → teinte-catégorie.
export function CatPastille({ tint = 'ciel', icon: IconCmp, size = 30, open = false }) {
  const t = CAT_TINTS[tint] || CAT_TINTS.ciel
  return (
    <span
      aria-hidden="true"
      style={{
        flex: '0 0 auto', width: size, height: size, borderRadius: 8,
        background: open ? 'rgba(255,255,255,.18)' : t.bg,
        color: open ? '#fff' : t.ico,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {IconCmp && <IconCmp size={Math.round(size * 0.57)} strokeWidth={1.8} />}
    </span>
  )
}

// FicheAccordion — section repliable blanc franc + pastille teinte-catégorie FIXE.
// Remplace le SectionCard teinté-par-bloc (fond décoratif interdit, DESIGN §7).
// Header = bouton (chevron + pastille + titre Fraunces + sous-titre gris court).
// `title` reste le SEUL texte du nœud titre (les tests matchent le titre exact).
export function FicheAccordion({ tint, icon, title, subtitle, open = true, onToggle, children }) {
  return (
    <div
      className="fbz-card"
      style={{
        background: DS.card,
        border: `0.5px solid ${DS.border}`,
        borderRadius: DS.radiusCard,
        marginBottom: 14,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 11,
          width: '100%', textAlign: 'left', cursor: 'pointer',
          background: open ? DS.marine : DS.card, border: 'none',
          padding: '14px 18px',
          fontFamily: DS.fontBody,
        }}
      >
        <ChevronDown
          size={18}
          strokeWidth={2}
          aria-hidden="true"
          style={{
            flex: '0 0 auto', color: open ? '#fff' : DS.textMuted,
            transition: `transform ${DS.durBase}`,
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
        <CatPastille tint={tint} icon={icon} open={open} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 16,
              color: open ? '#fff' : DS.marineDark, lineHeight: 1.2,
            }}
          >
            {title}
          </span>
          {subtitle && (
            <span style={{ display: 'block', fontSize: 12.5, color: open ? 'rgba(255,255,255,.7)' : DS.textSecondary, marginTop: 3, lineHeight: 1.45 }}>
              {subtitle}
            </span>
          )}
        </span>
      </button>
      {open && <div style={{ padding: '4px 18px 18px' }}>{children}</div>}
    </div>
  )
}

// Sous-section interne d'un accordéon (ex. Administration → Identité / Propriétaire
// / Stripe). PAS une carte (on est déjà dans une carte blanche) : titre Fraunces +
// séparateur hairline en tête, pour une hiérarchie sans empilement de cartes.
// `title` reste le seul texte du nœud titre (compat tests getByText).
export function FicheSubSection({ icon: IconCmp, title, intro, first = false, children }) {
  return (
    <div
      style={{
        marginTop: first ? 4 : 18,
        paddingTop: first ? 0 : 16,
        borderTop: first ? 'none' : `1px solid ${DS.border}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: intro ? 5 : 12 }}>
        {IconCmp && <IconCmp size={16} strokeWidth={1.8} aria-hidden="true" style={{ flex: '0 0 auto', color: DS.marine }} />}
        <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 14.5, color: DS.marineDark }}>{title}</span>
      </div>
      {intro && <div style={{ fontSize: 12, color: DS.textSecondary, marginBottom: 13, lineHeight: 1.5 }}>{intro}</div>}
      {children}
    </div>
  )
}

// FicheAlert — alerte en LIGNE FINE (liséré gauche 3px), jamais un encadré empilé
// (DESIGN §7 : le liséré vertical d'alerte est un cliché — ici on l'assume comme
// motif validé §8 ter pour les fiches). Sens : warning (ambre) · danger (rouge) ·
// info (ciel). Présentation pure ; `children` = le message.
const ALERT_TONES = {
  warning: { bg: DS.warningBg, border: '#f1d9ac', accent: DS.warning, text: DS.warningText, Icon: AlertTriangle },
  danger:  { bg: DS.dangerBg,  border: '#f3c6c6', accent: DS.danger,  text: DS.dangerText,  Icon: AlertCircle },
  info:    { bg: DS.cielSoft,  border: '#c8e0f5', accent: DS.ciel,    text: DS.marineDeep,  Icon: Info },
  success: { bg: DS.successBg, border: '#bfe6d6', accent: DS.success, text: DS.successText, Icon: CheckCircle2 },
}
export function FicheAlert({ tone = 'info', children, style, testId }) {
  const t = ALERT_TONES[tone] || ALERT_TONES.info
  const Ico = t.Icon
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex', gap: 9, alignItems: 'flex-start',
        padding: '9px 12px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.45,
        background: t.bg, border: `0.5px solid ${t.border}`, borderLeft: `3px solid ${t.accent}`,
        color: t.text, fontFamily: DS.fontBody,
        ...style,
      }}
    >
      <Ico size={15} strokeWidth={1.9} aria-hidden="true" style={{ flex: '0 0 auto', marginTop: 1 }} />
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  )
}

// Bouton primaire DA (action clé) — corail plein, s'enfonce au clic (.fbz-press).
export function PrimaryButton({ children, onClick, disabled, type = 'button', style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={disabled ? undefined : 'fbz-press'}
      style={{
        background: disabled ? '#cdd5df' : DS.corail,
        color: '#fff', border: 'none', borderRadius: DS.radiusControl,
        padding: '9px 18px', fontSize: 12.5, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: DS.fontBody,
        ...style,
      }}
    >
      {children}
    </button>
  )
}

// Bouton secondaire DA — blanc, contour fin, texte marine.
export function SecondaryButton({ children, onClick, disabled, type = 'button', tone = 'marine', style }) {
  const color = tone === 'danger' ? DS.danger : DS.marine
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={disabled ? undefined : 'fbz-press'}
      style={{
        background: DS.card,
        color, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl,
        padding: '9px 16px', fontSize: 12.5, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: DS.fontBody,
        ...style,
      }}
    >
      {children}
    </button>
  )
}
